import { useState } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { cn } from '@rocco/utils/cn';
import ProductTagsDisplay from '@modules/product-tagging/components/product-tags-display/ProductTagsDisplay';
import ProductTaggingShell from '@modules/product-tagging/components/product-tagging-shell/ProductTaggingShell';
import { useProductTagging } from '@modules/product-tagging/context';
import TagPopover from '@modules/page-builder/component/detail-article/tag-popover/TagPopover';
import HoverButtonsImage from '@modules/page-builder/component/detail/detail-single-image-shell/HoverButtonsImage';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import { getImageAttrs } from '~/modules/images';
import { REVAMP_FIGURE_CAPTION } from '../../util/article-prose';
import { REVAMP_IMAGE_COLUMN_SIZES } from '../../util/revamp-image-sizes';
import { describeImage } from '../../util/describe-image';
import {
    ARTICLE_PHOTO_ATTR,
    useArticlePhotoViewer,
} from '../ArticlePhotoViewer';
import type { ImageColumnProps } from '@modules/page-builder/types';

interface RevampImageFigureProps {
    column: ImageColumnProps;
    /** The figure's rendered width as a `sizes` string; defaults to the 900 body column. */
    sizes?: string;
    className?: string;
    imgClassName?: string;
    captionClassName?: string;
    /**
     * Pin the figure to a ratio and crop to it, e.g. `1520 / 800`. Without it
     * the figure takes the upload's own ratio, which is right for a figure set
     * in a column of prose but wrong where the design fixes the box — a wide
     * module handed a 3:2 photograph runs half a screen taller than drawn.
     */
    aspectRatio?: string;
}

/**
 * Shared image + caption figure for the revamp image modules — the image at
 * its column width with the small caption below (Figma "Description", 13.9px
 * at 80% ink), carrying the article's product tags and the legacy photo
 * module's hover actions.
 *
 * The caption is the editor's, verbatim. It used to be prefixed with a running
 * "( N )" figure number, which only the single-image and image-and-paragraph
 * modules ever produced -- so a body carrying any of the multi-image modules
 * showed one numbered caption adrift among unnumbered ones, which reads worse
 * than no numbering at all. The image-with-CTA-buttons
 * design is a separate page-builder module to come (BC-4720 follow-up) so
 * editors configure buttons per instance.
 */
export const RevampImageFigure = ({
    column,
    sizes = REVAMP_IMAGE_COLUMN_SIZES,
    className,
    imgClassName,
    captionClassName,
    aspectRatio,
}: RevampImageFigureProps) => {
    const { mobile } = useAppDisplayModeContext();
    const { canCreateTag } = useProductTagging();
    const openPhotoViewer = useArticlePhotoViewer();
    // Same experiment gate as the legacy article image shell, so the revamp
    // doesn't silently take the dots to 100% exposure. Taggers are excluded so
    // the dots stay clear of the tag-authoring overlay, as legacy does.
    const tagDotsEnabled = useABTestIsOn('product_tagging_dots');
    // The dots' expanded panel covers the same corner as the hover actions.
    const [showHoverButtons, setShowHoverButtons] = useState(true);

    if (!column.imageUrl) return null;

    const { width, height } = column.dimension ?? {};
    const caption = column.caption ?? '';
    // Desktop overlays positioned dots on the image; mobile lists the approved
    // tags under it — the same split (and the same two data sources) the legacy
    // article uses.
    const { libraryImage, tags, uniqueApprovedTags, dimension } = column;
    const tagDots =
        tagDotsEnabled &&
        !canCreateTag &&
        !mobile &&
        libraryImage &&
        tags?.length
            ? { libraryImage, tags }
            : null;
    const tagList =
        mobile && uniqueApprovedTags?.length && dimension
            ? { tags: uniqueApprovedTags, dimension }
            : null;
    // Boosting rewrites the stored image, so it stays a desktop-only staff
    // action; the overlay itself renders nothing for everyone else.
    const canBoostImage = !mobile && !!libraryImage?.ID;

    const imageBox = (
        <div
            // The actions sit hidden on the image until it is hovered, the
            // reveal the legacy shell drives from its own container rule.
            // Important, because the buttons' resting `opacity: 0` is a fela
            // rule: fela injects unlayered CSS, which outranks anything in
            // Tailwind's utilities layer however specific it is.
            // `group` so the tile action panel's own hover reveal fires: its
            // strips rest at `md:opacity-0` and come up on `md:group-hover`,
            // and with no group above them they never appeared on an article's
            // pictures the way they do on a project's.
            // The tag markers come up on the same hover, but they name this
            // group themselves rather than being named here — every figure
            // that hosts the tagging shell needs that reveal, and only this
            // one had it.
            className="group relative w-full [&:hover_.hover-buttons-image]:visible! [&:hover_.hover-buttons-image]:opacity-100!"
        >
            {/* Body images sit below the fold on every layout, so they load
                lazily; the hero is the only image on the critical path.
                Wrapped in a button when a viewer is mounted, so the picture
                opens full-size — and left as a plain image when one is not,
                rather than offering a control that does nothing. The overlays
                below are siblings of the button, so a press on a tag dot or a
                hover action still reaches them instead of opening the
                viewer. */}
            <button
                type="button"
                onClick={
                    openPhotoViewer
                        ? (event) => {
                              const image =
                                  event.currentTarget.querySelector('img');
                              if (image) openPhotoViewer(image);
                          }
                        : undefined
                }
                // Not a control without a viewer to open: no pointer, no tab
                // stop, and nothing announced.
                {...(openPhotoViewer
                    ? { 'aria-label': 'View image full size' }
                    : { tabIndex: -1, 'aria-hidden': true })}
                className={cn(
                    'block w-full border-0 bg-transparent p-0',
                    openPhotoViewer ? 'cursor-zoom-in' : 'cursor-default',
                )}
            >
                <img
                    {...getImageAttrs(column.imageUrl, { sizes })}
                    alt={describeImage(
                        column.caption,
                        column.libraryImage?.Title,
                    )}
                    loading="lazy"
                    decoding="async"
                    {...{ [ARTICLE_PHOTO_ATTR]: '' }}
                    data-article-photo-src={column.imageUrl}
                    data-article-photo-caption={caption}
                    data-article-photo-id={libraryImage?.ID ?? undefined}
                    // The corner every image on the site carries. On the image
                    // rather than its wrapper: the wrapper holds the tag dots
                    // and the hover actions, and clipping it would cut their
                    // popovers.
                    className={cn(
                        // Bottom-anchored: the photo credit is burned into the
                        // foot of the upload, and a centred cover crop takes it
                        // off along with the top of the frame.
                        'w-full rounded-tile object-cover object-bottom',
                        imgClassName,
                    )}
                    {...(width && height ? { width, height } : {})}
                    {...(aspectRatio || (width && height)
                        ? {
                              style: {
                                  aspectRatio:
                                      aspectRatio ?? `${width} / ${height}`,
                              },
                          }
                        : {})}
                />
            </button>
            {/* The authoring surface behind the "Start tagging" action. */}
            {canCreateTag && libraryImage?.ID && (
                <ProductTaggingShell libraryImage={libraryImage} tags={tags} />
            )}
            {tagDots && (
                <ProductTagsDisplay
                    libraryImage={tagDots.libraryImage}
                    tags={tagDots.tags}
                    onToggle={(toggled) => setShowHoverButtons(!toggled)}
                />
            )}
            {libraryImage?.ID && showHoverButtons && (
                <HoverButtonsImage
                    photoID={libraryImage.ID}
                    libraryItemId={libraryImage.LibraryItemID ?? undefined}
                    clickableLink={libraryImage.ClickableLink || undefined}
                    imageURL={column.imageUrl}
                    downloadLink={libraryImage.DownloadLink || undefined}
                    taggingEnabled={canCreateTag}
                />
            )}
        </div>
    );

    return (
        <figure
            className={cn('m-0 flex w-full flex-col gap-4 md:gap-6', className)}
        >
            {canBoostImage && libraryImage?.ID ? (
                <BoostResolutionOverlay
                    libraryImageId={libraryImage.ID}
                    currentImageUrl={column.imageUrl}
                    displayWidth={width ?? 0}
                    displayHeight={height ?? 0}
                    columnId={column.id}
                    wasUpscaled={!!libraryImage.WasUpscaled}
                    className="w-full"
                >
                    {imageBox}
                </BoostResolutionOverlay>
            ) : (
                imageBox
            )}
            {tagList && (
                <TagPopover tags={tagList.tags} dimension={tagList.dimension} />
            )}
            {caption && (
                <figcaption
                    className={cn(REVAMP_FIGURE_CAPTION, captionClassName)}
                >
                    {caption}
                </figcaption>
            )}
        </figure>
    );
};
